/**
 * Pounds <-> pence at the UI edge ONLY (the money policy: integer pence everywhere else). Mirrors the
 * helpers in features/services/ServiceModal so the till's £ inputs convert exactly the same way. These
 * never do business math — they parse what the user typed into the integer-pence intent the API expects;
 * every total/balance the till DISPLAYS is a server number, never recomputed here.
 */

/** "1400" (pence) → "14.00" for a £ input; null → "". */
export function penceToPounds(pence: number | null): string {
  if (pence == null) return '';
  return (pence / 100).toFixed(2);
}

/** "14" | "14.5" | "£14.00" → 1400 pence; null if not a valid non-negative amount. */
export function poundsToPence(input: string): number | null {
  const cleaned = input.replace(/[£,\s]/g, '');
  if (!cleaned) return null;
  const value = Number(cleaned);
  if (!Number.isFinite(value) || value < 0) return null;
  return Math.round(value * 100);
}

/**
 * WS14 — the amount the till charges at settle when tips are captured. The intent charges the SERVICE
 * remaining (the server-figured outstanding) PLUS the sum of captured tips, on ONE payment. The tip rides
 * issue-receipt (not the intent body); the server carves the service portion back out
 * (= payment.amountPence − Σtips) and reconciles shown==charged. This is a pure sum — NOT business money
 * math (every per-tender reduction was already a server figure); it just adds the gratuity the customer
 * chose on top of what's owed. Tips are integer pence > 0 by construction (the modal enforces it).
 */
export function settleChargePence(
  serviceRemainingPence: number,
  tips: { amountPence: number }[],
): number {
  const tipTotal = tips.reduce((s, t) => s + t.amountPence, 0);
  return serviceRemainingPence + tipTotal;
}
